/**
 * @file accessibilityHelpers.js
 * @fileOverview Controls fast tabbing & selection
 * @author Smartmatic
 * @creationDate 2019-12-26
 * @modificationDate 2025-03-14
 */

/**
 * Controls tab key navigation to reduce rapid tabbing.
 *
 * Throttles the tab key navigation by switching eligibility for tabbing, on
 * keyup and keydown events. Useful in scenarios where too rapid successive tabbing
 * could cause confusing or erratic behavior in UI. Bound to the 'keydown' and 'keyup' events.
 *
 * @returns {undefined} Does not have a return value.
 */
/* If press and hold tab, should only tab once. This is a project requirement. */
export function throttleTabbing() {
  let eligibleForTab = true;

  /**
   * Handles keydown event for 'Tab' key.
   *
   * This function checks if the key event is a 'Tab' key press. If it's a 'Tab' key press and
   * eligibleForTab is true, it resets eligibleForTab to false; if eligibleForTab is false, it
   * prevents the default key event action.
   *
   * @param {object} e - The key event object.
   */
  const handleKeyDown = e => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (eligibleForTab) {
        eligibleForTab = false;
      } else {
        e.preventDefault();
      }
    }
  };

  /**
   * Handles keyUp events for particular keys and sets eligibleForTabe to true if conditions are met.
   *
   * This function is dedicated to observing keyUp events. It checks if the key pressed is the 'Tab'
   * key. When the event is initiated through a 'Tab' key press and the condition, 'eligibleForTab'
   * is false, 'eligibleForTab' will be set to true for future references. Applications can include
   * but are not limited to, inputs requiring a user action confirmation before allowing tabbing
   * forward like multi-factor authentication codes, coupon code entries, etc...
   *
   * @param {object} e - The keyup event object.
   */
  const handleKeyUp = e => {
    if ((e.key === 'Tab' || e.keyCode === 9) && !eligibleForTab) {
      eligibleForTab = true;
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
}

/**
 * Prevents the select menu from moving more than once when holding the up or down arrow keys.
 *
 * The function listens to the 'keydown' and 'keyup' events on the window object. When the events
 * are fired, corresponding handlers assess if the active document element is a 'select-one'
 * dropdown. If so, the handlers then check if the key event is for an up or down arrow key. If
 * the arrow key is eligible for tab movement (initially set to true), it becomes ineligible after
 * one movement. This prevents further movement while the key is held down. On 'keyup', the arrow
 * key's eligibility for tab movement is reset to true, allowing it to move the selection again on
 * subsequent 'keydown' events.
 *
 * Note that this function does not explicitly take in parameters, so no @param annotations are
 * added according to the project's requirements for JSDocs.
 *
 * It directly returns nothing, as it only sets event listeners and thus there are no @returns
 * annotations according to the project's requirements for JSDocs.
 */
export function throttleSelectUpAndDown() {
  let upEligibleForTab = true;
  let downEligibleForTab = true;

  /**
   * Handles keyDown events for select-one type elements to conditionally prevent default.
   * The function checks if the active document element is of 'select-one' type. If so, it determines
   * the key pressed (ArrowUp/ArrowDown) and accordingly manipulates global flags and preventDefault
   * method calling. This function could be used in scenarios where you need to handle interactive
   * navigation with keyboard for 'select-one' type elements.
   *
   * @param {object} e - The event object
   */
  const handleKeyDown = e => {
    if (document.activeElement.type === 'select-one') {
      /* up arrow: */
      if (e.key === 'ArrowUp' || e.keyCode === 38) {
        if (upEligibleForTab) {
          upEligibleForTab = false;
        } else {
          e.preventDefault();
        }
      }
      /* down arrow: */
      if (e.key === 'ArrowDown' || e.keyCode === 40) {
        if (downEligibleForTab) {
          downEligibleForTab = false;
        } else {
          e.preventDefault();
        }
      }
    }
  };

  /**
   * Handles 'keyup' events to determine if the active element is eligible for tab selection
   * based on user's arrow key presses.
   *
   * This function checks if the current active element on the document is a 'select-one' type. If
   * it is, then it determines if the key pressed was either the up arrow key or down arrow key. If
   * these conditions are met and the respective up/down tab eligibility flags are currently false,
   * then the flags are set to true.
   *
   * @param {object} e - The event object provided by the 'keyup' event listener.
   */
  const handleKeyUp = e => {
    if (document.activeElement.type === 'select-one') {
      if ((e.key === 'ArrowUp' || e.keyCode === 38) && !upEligibleForTab) {
        upEligibleForTab = true;
      }
      if ((e.key === 'ArrowDown' || e.keyCode === 40) && !downEligibleForTab) {
        downEligibleForTab = true;
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
}
